import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Badge, Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { modelTypesByApiModelValue } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import {
  compact,
  Dictionary,
  filter,
  flatMap,
  forEach,
  groupBy,
  includes,
  isEmpty,
  join,
  map,
  reduce,
  size,
  some,
  sortBy,
  values,
} from 'lodash';
import pluralize from 'pluralize';
import React, { useMemo, useState } from 'react';
import { AssignmentByStain } from 'utils/assignmentManagement';
import { humanize } from 'utils/helpers';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { SlideRegistrationDetailsByOrchestration } from 'utils/slideRegistrations';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { inputCategoryLabels, MissingInputsBySlideId } from '../calculateFeaturesInputs';
import SlideInputValidationSection from './SlideInputValidationSection';
import { getSlideInputValidations } from './slideInputValidations';
import { getSlidesModelsData, ModelSelection } from './utils';

export interface SummaryProps {
  modelsType: string[];
  slides: Dictionary<SlideInferenceResults>;
  selectedModelOrchestrations: OrchestrationBySlideByType;
  selectedOrchestrationsByResultName: OrchestrationBySlideByResultName[];
  selectedAssignments: AssignmentByStain;
  removeSelectedModels: (slideIds: string[]) => void;
  displayAssignmentAnnotations?: boolean;
  selectedSlideRegistrationsByOrchestration?: SlideRegistrationDetailsByOrchestration;
  missingInputsBySlideId?: MissingInputsBySlideId;
  showValidationMessages?: boolean;
}
const defaultRowsPerPage = 10;
const pageSizeOptions = [10];

const legendSlidesSummary = [
  {
    icon: <CheckIcon />,
    description: 'Inference Result',
  },
  {
    icon: 'A',
    description: 'Assignment Result',
  },
];

const Summary: React.FC<React.PropsWithChildren<SummaryProps>> = ({
  modelsType,
  slides,
  selectedModelOrchestrations,
  selectedOrchestrationsByResultName,
  selectedAssignments,
  removeSelectedModels,
  displayAssignmentAnnotations = false,
  selectedSlideRegistrationsByOrchestration,
  missingInputsBySlideId,
  showValidationMessages = true,
}) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultRowsPerPage,
  });
  const [paginationModelStain, setPaginationModelStain] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultRowsPerPage,
  });
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  const slidesModels = useMemo(
    () =>
      getSlidesModelsData(
        slides,
        selectedModelOrchestrations,
        selectedOrchestrationsByResultName,
        selectedAssignments,
        modelsType,
        selectedSlideRegistrationsByOrchestration
      ),
    [
      slides,
      selectedModelOrchestrations,
      selectedOrchestrationsByResultName,
      selectedAssignments,
      modelsType,
      selectedSlideRegistrationsByOrchestration,
    ]
  );

  const slidesByCaseLabel = groupBy(sortBy(slidesModels, ['caseLabel', 'stainingType']), 'caseLabel');

  // Sort slides by case label and count of models in case
  const sortedSlidesModelsCountByCase = sortBy(values(slidesByCaseLabel), (slidesModel) => {
    return reduce(
      modelsType,
      (count, modelType) => {
        forEach(slidesModel, (slideModel) => {
          if (slideModel[modelType]) count++;
        });
        return count;
      },
      0
    );
  });
  const sortedSlidesModelsCount = flatMap(sortedSlidesModelsCountByCase);

  const slidesByCase = groupBy(slidesModels, 'caseLabel');

  const slideInputValidations = useMemo(
    () =>
      getSlideInputValidations({
        slidesModels,
        modelsType,
        missingInputsBySlideId,
        slidesById: slides,
      }),
    [slidesModels, modelsType, missingInputsBySlideId, slides]
  );

  const errorValidations = useMemo(() => filter(slideInputValidations, { severity: 'error' }), [slideInputValidations]);
  const warningValidations = useMemo(
    () => filter(slideInputValidations, { severity: 'warning' }),
    [slideInputValidations]
  );
  const errorSlidesCount = size(flatMap(errorValidations, (validation) => validation.slideIds));
  const warningSlidesCount = size(flatMap(warningValidations, (validation) => validation.slideIds));
  const hasInputIssuesSummary = showValidationMessages && (errorSlidesCount > 0 || warningSlidesCount > 0);

  const sortedSlidesModelsCountWithRegistrations = useMemo(
    () =>
      map(sortedSlidesModelsCount, (slideModel) => {
        const slideRegistrationsPerStain: { [stainId: string]: boolean } = {};
        forEach(slideModel.registrationsInCasePerStain, (registrationExists, stain) => {
          slideRegistrationsPerStain[`registration-${stain}`] = registrationExists;
        });
        const registrationsCount = size(compact(values(slideRegistrationsPerStain)));
        const caseOtherSlides = filter(
          slidesByCase[slideModel.caseLabel],
          (slidesData) => slidesData.slideId !== slideModel.slideId
        );

        return {
          ...slideModel,
          ...slideRegistrationsPerStain,
          registrationCounts: `${registrationsCount}/${size(caseOtherSlides)}`,
        };
      }),
    [sortedSlidesModelsCount, slidesByCase]
  );
  const slidesByStain = groupBy(slidesModels, 'stainingType');

  const countModelsByStain: Record<string, Record<string, number>> = {};
  forEach(slidesByStain, (slidesOfStain, stain) => {
    countModelsByStain[stain] = { slidesCount: slidesOfStain.length };
    forEach(modelsType, (modelType) => {
      countModelsByStain[stain][modelType] = 0;
      forEach(slidesOfStain, (slide) => {
        if (slide[modelType]) {
          countModelsByStain[stain][modelType]++;
        }
      });
    });

    // add registration count
    countModelsByStain[stain].registrations = 0;
    forEach(slidesOfStain, (slide) => {
      const slideRegistrationCount = size(compact(values(slide.registrationsInCasePerStain)));
      countModelsByStain[stain].registrations += slideRegistrationCount;
    });
  });

  const countModelsByStainData = map(countModelsByStain, (countModels, stain) => {
    return {
      stain,
      ...countModels,
    };
  });

  const columnsStain: GridColDef[] = [
    {
      field: 'stain',
      headerName: 'Stain',
      width: 80,
      valueFormatter: (value) => (isLoadingStainTypeOptions ? 'Loading...' : stainTypeIdToDisplayName(value)),
    },
    {
      field: 'slidesCount',
      headerName: 'Slides Count',
      headerAlign: 'center',
      width: 100,
      type: 'number',
      align: 'center',
    },
  ];

  const modelsColumnsStain: GridColDef[] = map(modelsType, (modelType) => {
    return {
      field: modelType,
      headerAlign: 'center',
      headerName: modelTypesByApiModelValue[modelType]?.text ?? humanize(modelType),
      width: 80,
      type: 'number',
      align: 'center',
    };
  });

  const registrationColumnStain: GridColDef = {
    field: 'registrations',
    headerName: 'Registrations',
    width: 150,
    type: 'number',
    align: 'center',
  };

  const columns: GridColDef[] = [
    { field: 'caseLabel', headerName: 'Case Label', width: 100 },
    {
      field: 'stainingType',
      headerName: 'Stain',
      width: 80,
      valueFormatter: (value) => (isLoadingStainTypeOptions ? 'Loading...' : stainTypeIdToDisplayName(value)),
    },
    { field: 'slideId', headerName: 'Slide ID', width: 200 },
    {
      field: 'inputIssues',
      headerName: 'Input Issues',
      width: 90,
      sortable: false,
      renderCell: (params) => {
        const slideId = params.row.slideId as string;
        const missingInputs = missingInputsBySlideId?.[slideId];
        const hasMissingSelectedInputs = some(slideInputValidations, (validation) => {
          if (validation.type !== 'missing_slide_selections') {
            return false;
          }
          return some(flatMap(values(validation.slidesByStain)), (slide) => slide.slideId === slideId);
        });
        const showErrorIcon = hasMissingSelectedInputs;
        const issueMessages = compact([
          hasMissingSelectedInputs ? 'Selections missing: add inference models or assignments' : null,
          !isEmpty(missingInputs)
            ? `Missing ${pluralize('input', size(missingInputs))}: ${join(
                map(missingInputs, (input) => inputCategoryLabels[input] ?? humanize(input)),
                ', '
              )}`
            : null,
        ]);
        if (isEmpty(issueMessages)) {
          return null;
        }
        return (
          <Tooltip title={join(issueMessages, '; ')}>
            {showErrorIcon ? (
              <ErrorOutlineIcon color="error" fontSize="small" />
            ) : (
              <WarningAmberIcon color="warning" fontSize="small" />
            )}
          </Tooltip>
        );
      },
    },
  ];

  const modelsColumns: GridColDef[] = map(modelsType, (modelType) => {
    return {
      field: modelType,
      headerAlign: 'center',
      headerName: modelTypesByApiModelValue[modelType]?.text ?? humanize(modelType),
      width: 70,
      type: 'boolean',
      renderCell: (params) => {
        const modelSelected = params.row[modelType] as ModelSelection;

        return (
          <Typography margin="auto" variant="body2">
            {modelSelected?.inference && modelSelected?.assignment ? (
              <Badge badgeContent={'A'}>
                <CheckIcon />
              </Badge>
            ) : modelSelected?.assignment ? (
              'A'
            ) : modelSelected?.inference ? (
              <CheckIcon />
            ) : params.row.countModelsInSlide > 0 && countModelsByStain?.[params.row.stainingType]?.[modelType] > 0 ? (
              <ClearIcon />
            ) : (
              '--'
            )}
          </Typography>
        );
      },
    };
  });

  const registrationColumns: GridColDef[] = [
    {
      field: 'registrationCounts',
      headerAlign: 'center',
      headerName: 'Registration Count',
      width: 150,
      align: 'center',
    },
    ...map(countModelsByStainData, (stainData): GridColDef => {
      const stainDisplayName = stainTypeIdToDisplayName(stainData.stain);

      return {
        field: `registration-${stainData.stain}`,
        headerAlign: 'center',
        headerName: `Registration ${stainDisplayName}`,
        width: 150,
        type: 'boolean',
        renderCell: (params) => {
          const caseLabel = params.row['caseLabel'];
          const caseOtherSlides = filter(
            slidesByCase[caseLabel],
            (slidesData) => slidesData.slideId !== params.row.slideId
          );
          const caseOtherStains = map(caseOtherSlides, 'stainingType');
          const hasRegistrationWithStain = params.row[params.field];

          return (
            <Typography margin="auto" variant="body2">
              {!includes(caseOtherStains, stainData.stain) ? (
                '--'
              ) : hasRegistrationWithStain ? (
                <CheckIcon />
              ) : (
                <ClearIcon />
              )}
            </Typography>
          );
        },
      };
    }),
  ];

  const actionColumns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      sortable: false,
      editable: false,
      width: 60,
      cellClassName: 'actions',
      getActions: (params) => {
        const slideId = params.row.slideId;

        const action = [
          <Tooltip
            placement="top-start"
            title="Remove selected artifacts results"
            key={`remove-selected-models-${params.id}`}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                removeSelectedModels([slideId]);
              }}
            >
              <CancelOutlinedIcon />
            </IconButton>
          </Tooltip>,
        ];

        return action;
      },
    },
  ];

  return (
    <Grid container spacing={1} direction="column">
      <Grid container spacing={1} direction="column">
        <Grid item xs={12} mt={1} width={'100%'}>
          <Grid item mb={1} display="flex" alignItems="center" gap={1}>
            <Typography>Slides Summary:</Typography>
            {hasInputIssuesSummary && (
              <Alert severity={errorSlidesCount > 0 ? 'error' : 'warning'}>
                {errorSlidesCount > 0 && (
                  <Typography variant="body2">
                    {errorSlidesCount} slide{errorSlidesCount === 1 ? '' : 's'} missing selections
                  </Typography>
                )}
                {warningSlidesCount > 0 && (
                  <Typography variant="body2">
                    {warningSlidesCount} slide{warningSlidesCount === 1 ? '' : 's'} missing inputs
                  </Typography>
                )}
              </Alert>
            )}
          </Grid>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minWidth: 0 }}>
            <DataGrid
              autoHeight
              getRowId={(row) => row?.slideId}
              rows={sortedSlidesModelsCountWithRegistrations ?? []}
              columns={[...columns, ...modelsColumns, ...registrationColumns, ...actionColumns]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={pageSizeOptions}
              pagination
              paginationMode="client"
            />
          </Box>
        </Grid>
        {displayAssignmentAnnotations && (
          <Grid item xs={12} mt={1} width={'100%'}>
            {map(legendSlidesSummary, (legend) => (
              <Grid container spacing={1} alignItems="center" key={legend.description}>
                <Grid item xs={0.5}>
                  {legend.icon}
                </Grid>
                <Grid item>{' - '}</Grid>
                <Grid item>{legend.description}</Grid>
              </Grid>
            ))}
          </Grid>
        )}
        <Grid item xs={12} mt={1} width={'100%'}>
          <Grid item mb={1}>
            <Typography>Stain Summary:</Typography>
          </Grid>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minWidth: 0, mb: 1 }}>
            <DataGrid
              autoHeight
              getRowId={(row) => row?.stain}
              rows={countModelsByStainData ?? []}
              columns={[...columnsStain, ...modelsColumnsStain, registrationColumnStain]}
              paginationModel={paginationModelStain}
              onPaginationModelChange={setPaginationModelStain}
              pageSizeOptions={pageSizeOptions}
              pagination
              paginationMode="client"
            />
          </Box>
        </Grid>
        {showValidationMessages && !isEmpty(slideInputValidations) && (
          <Grid item xs={12} mt={1} width={'100%'}>
            {map(errorValidations, (validation) => (
              <SlideInputValidationSection
                key={validation.type}
                validation={validation}
                removeSelectedModels={removeSelectedModels}
              />
            ))}
            {map(warningValidations, (validation) => (
              <SlideInputValidationSection
                key={validation.type}
                validation={validation}
                removeSelectedModels={removeSelectedModels}
              />
            ))}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default Summary;
